/**
 * PatientReviewTable
 *
 * Shared data table for displaying new patient review items.
 * Derives columns from the items in the current page.
 */

import { useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { TARGET_FIELD_LABELS } from '../../../types';
import type { PatientReviewTableProps } from '../ImportReviewWizard.types';

// =============================================================================
// Column Configuration
// =============================================================================

interface ColumnDef {
  key: string;
  label: string;
  minWidth: number;
}

/** Keys that are metadata, not patient data columns */
const METADATA_KEYS = new Set([
  'extracted_patient_id',
]);

/**
 * Flatten nested objects (e.g. `insurance: { payer_name, policy_number, … }`)
 * into top-level keys so each field gets its own column.
 * Primitive values are kept as-is; nested objects are spread and the parent key removed.
 */
function flattenPatient(patient: Record<string, unknown>): Record<string, string | null | undefined> {
  const result: Record<string, string | null | undefined> = {};
  for (const [key, value] of Object.entries(patient)) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
        result[nestedKey] = nestedValue == null ? null : String(nestedValue);
      }
    } else {
      result[key] = value == null ? null : String(value);
    }
  }
  return result;
}

function getMinWidthForField(field: string): number {
  switch (field) {
    case 'address':
    case 'email':
    case 'emergency_contact_name':
    case 'insurance_policy_number':
      return 180;
    case 'emergency_contact_relationship':
      return 200;
    case 'first_name':
    case 'last_name':
    case 'preferred_name':
    case 'payer_name':
      return 140;
    case 'dob':
    case 'gender':
    case 'sex':
    case 'pronouns':
    case 'race':
    case 'zip':
    case 'state':
      return 100;
    default:
      return 120;
  }
}

/** Format a snake_case key into a Title Case label */
function formatFieldLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Use a known label if available, otherwise format the raw key */
function getLabelForField(key: string): string {
  return (TARGET_FIELD_LABELS as Record<string, string>)[key] ?? formatFieldLabel(key);
}

// =============================================================================
// Sticky Column Styles
// =============================================================================

const stickyColumnSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: 'background.paper',
} as const;

const stickyHeaderColumnSx = {
  ...stickyColumnSx,
  zIndex: 3,
} as const;

// =============================================================================
// Component
// =============================================================================

export const PatientReviewTable = ({ patients, rowOffset = 0 }: PatientReviewTableProps) => {
  // The API may return nested objects (e.g. insurance: { payer_name, ... }) that the
  // generated NewPatientItem type doesn't reflect. Cast to Record so flattenPatient
  // can safely spread those nested fields into top-level columns.
  const flatPatients = useMemo(
    () => patients.map((p) => flattenPatient(p as Record<string, unknown>)),
    [patients],
  );

  const allColumns = useMemo<ColumnDef[]>(() => {
    if (flatPatients.length === 0) return [];

    const keysInOrder: string[] = [];
    const keysSeen = new Set<string>();

    for (const patient of flatPatients) {
      for (const key of Object.keys(patient)) {
        if (!METADATA_KEYS.has(key) && !keysSeen.has(key)) {
          keysSeen.add(key);
          keysInOrder.push(key);
        }
      }
    }

    // Promote name columns to the front
    const priorityKeys = ['first_name', 'last_name', 'preferred_name', 'middle_name', 'dob'];
    const promoted = priorityKeys.filter((k) => keysSeen.has(k));
    const promotedSet = new Set(promoted);
    const rest = keysInOrder.filter((k) => !promotedSet.has(k));

    return [...promoted, ...rest].map((key) => ({
      key,
      label: getLabelForField(key),
      minWidth: getMinWidthForField(key),
    }));
  }, [flatPatients]);

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: 'calc(100vh - 360px)', overflow: 'auto' }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 56, ...stickyHeaderColumnSx }}>
              #
            </TableCell>
            {allColumns.map((col) => (
              <TableCell
                key={col.key}
                sx={{
                  fontWeight: 600,
                  minWidth: col.minWidth,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {flatPatients.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={allColumns.length + 1}
                align="center"
                sx={{ py: 4 }}
              >
                <Typography variant="body2" color="text.secondary">
                  No patients in this category.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            flatPatients.map((patient, idx) => (
              <TableRow key={patient.extracted_patient_id ?? idx} hover>
                <TableCell sx={stickyColumnSx}>{rowOffset + idx + 1}</TableCell>
                {allColumns.map((col) => {
                  const value = patient[col.key];
                  return (
                    <TableCell key={col.key} sx={{ whiteSpace: 'nowrap' }}>
                      {value ?? '\u2014'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
